'use client';

import { signOut } from 'next-auth/react';

import {
  SESSION_INVALID_ERROR,
  signedOutLandingPath,
  type FederatedLogoutReason
} from '@/lib/auth/signed-out-reasons';

// The reason vocabulary lives in `signed-out-reasons.ts`, which carries no
// `'use client'` directive because the server-rendered landing route reads it
// too (VALE-1859; that module's header has the failure it prevents). Re-exported
// here so the many existing importers of these names keep working.
export {
  ACCOUNT_NOT_PROVISIONED_ERROR,
  SESSION_INVALID_ERROR,
  SIGNED_OUT_PATH_SEGMENTS,
  SIGNED_OUT_REASON_BY_SEGMENT,
  signedOutLandingPath,
  type FederatedLogoutReason
} from '@/lib/auth/signed-out-reasons';

/**
 * Backend `code` on a 401 body identifying an unprovisioned account. Kept in
 * sync with `backend/src/auth/strategies/jwt.strategy.ts`
 * `ACCOUNT_NOT_PROVISIONED_CODE`.
 */
export const ACCOUNT_NOT_PROVISIONED_CODE = 'ACCOUNT_NOT_PROVISIONED';

/**
 * Build Keycloak's OIDC end-session (RP-initiated logout) URL.
 *
 * Terminating the *Keycloak* session — not just the NextAuth cookie — is what
 * breaks the login loop (LF-4870). When a validly-authenticated session is
 * unrecoverable (no `tenant_id`, or the backend persistently rejects a
 * freshly-refreshed token), clearing NextAuth alone doesn't help: Keycloak's
 * SSO cookie silently re-mints the same broken session on the next `signIn`.
 * Redirecting the browser through the end-session endpoint clears that SSO
 * cookie, and `post_logout_redirect_uri` lands the user back on a clean,
 * non-looping /login.
 *
 * Returns `null` when the Keycloak config env vars are absent (e.g. a preview
 * build) so callers can fall back to a plain landing on /login.
 */
export function buildKeycloakEndSessionUrl(
  idToken?: string,
  loginError: FederatedLogoutReason = SESSION_INVALID_ERROR
): string | null {
  const authUrl = process.env.NEXT_PUBLIC_AUTH_URL;
  const realm = process.env.NEXT_PUBLIC_KEYCLOAK_REALM;
  const clientId = process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID;
  const appUrl = process.env.NEXT_PUBLIC_APP_URL;

  if (!authUrl || !realm || !clientId || !appUrl) {
    return null;
  }

  const params = new URLSearchParams({
    client_id: clientId,
    post_logout_redirect_uri: `${appUrl}${signedOutLandingPath(loginError)}`
  });

  // id_token_hint lets Keycloak end the exact session without prompting its
  // "Do you want to log out?" confirmation interstitial.
  if (idToken) {
    params.set('id_token_hint', idToken);
  }

  return `${authUrl}/realms/${realm}/protocol/openid-connect/logout?${params}`;
}

/**
 * Module-level guard so concurrent triggers (a burst of 401s plus the
 * tenant-less session hook) only navigate away once.
 */
let logoutInFlight = false;

/**
 * Force a full logout for an UNRECOVERABLE session error: clear the NextAuth
 * session, then browser-redirect to Keycloak's end-session endpoint so the
 * Keycloak SSO cookie is cleared too and the next `signIn` cannot silently
 * re-mint the broken session. The user lands on `/login?error=SessionInvalid`,
 * via the `/signed-out/<reason>` hop Keycloak will actually admit as a
 * post-logout destination (see {@link SIGNED_OUT_PATH_SEGMENTS}). The fallback
 * below skips that hop deliberately: it is a local navigation Keycloak never
 * validates, so it can address the /login card directly.
 *
 * Deliberately distinct from the RECOVERABLE paths (SessionExpired /
 * OAuthCallback auto-retry, RefreshAccessTokenError signOut), which must NOT
 * end the Keycloak session — see the LF-4567 loop-breaker.
 */
export async function forceFederatedLogout(
  idToken?: string,
  loginError: FederatedLogoutReason = SESSION_INVALID_ERROR
): Promise<void> {
  if (typeof window === 'undefined') return;
  if (logoutInFlight) return;
  logoutInFlight = true;

  // Capture the end-session URL before clearing the session (the idToken is
  // passed in by the caller, read while the session was still present).
  const endSessionUrl = buildKeycloakEndSessionUrl(idToken, loginError);

  try {
    // Clear the NextAuth session cookie first (redirect:false — we own the
    // navigation). Clearing before we leave avoids a stale NextAuth session
    // bouncing the user off /login and back into the app on return. A
    // top-level navigation to Keycloak (below) is not a fetch, so no CORS.
    await signOut({ redirect: false });
  } catch {
    // Even if the NextAuth clear fails, still terminate the Keycloak session.
  }

  window.location.href = endSessionUrl ?? `/login?error=${loginError}`;
}
